import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn, IconButton } from '@c2fo/liquidity';
import { CalendarLinesIcon, PenIcon } from '@c2fo/liquidity/icons';
import {
  EditRateModalWithContext,
  useEditRateModalContext,
} from '@/enterprise/components/OfferActivity/EditRateModal/EditRateModalContext';
import {
  EditScheduleModalWithContext,
  useEditScheduleModalContext,
} from '@/enterprise/components/OfferActivity/EditScheduleModal/EditScheduleModalContext';
import { getOfferBuilderNavigationState } from '@/enterprise/components/OfferActivity/OfferActivityCards';
import useEnablePricing from '@/enterprise/components/OfferBuilder/useEnablePricing';
import OfferContent from '@/enterprise/components/OfferDetailsBanner/OfferContent';
import Skeleton from '@/enterprise/components/Skeleton';
import useEnableAutomations from '@/enterprise/components/useEnableAutomations';
import useEnableUnification from '@/enterprise/components/useEnableUnification';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { OfferModalWithContext, useOfferModalContext } from '@/enterprise/features/offerModal/OfferModalContext';
import { buildCustomScheduleSentenceFromExistingOfferSchedule } from '@/enterprise/features/offerModal/utils';
import { useMarketStatsStatus } from '@/lib/useMarketStatsStatus';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import getRenewalSchedule from '@/utils/getRenewalSchedule';
import getTakerMarketName from '@/utils/getTakerMarketName';
import handleNaN from '@/utils/handleNaN';
import useLocaleFormat from '@/utils/useLocaleFormat';

const useOfferDetailsContent = (takerMarket: TakerMarket) => {
  const { t } = useTranslation();
  const { asPercent } = useLocaleFormat();
  const percentClearing = handleNaN(
    takerMarket.clearingInvoiceAmount / (takerMarket.eligibleInvoiceAmount + takerMarket.awardedInvoiceAmount)
  );

  if (takerMarket.offerConfig.offerType === 'PREFERRED_TERM') {
    return {
      bgColor: 'bg-purple-100',
      bgNotificationColor: 'bg-purple-500/20',
      heading: t('offerDetails.yourOfferIsClearing', { formattedPercent: asPercent({ value: percentClearing }) }),
      subHeading: '',
    };
  }

  // market is disabled
  if (takerMarket.marketIsEnabled === false) {
    return {
      bgColor: 'bg-deepBlue-50',
      bgNotificationColor: 'bg-deepBlue-500/20',
      heading: t('offerDetails.marketIsNotEnabled', { marketName: getTakerMarketName(takerMarket) }),
      subHeading: t('offerDetails.marketIsNotEnabledSubHeading', { marketName: getTakerMarketName(takerMarket) }),
    };
  }

  // market is enabled, participation is off, no eligible invoices (future offer)
  if (takerMarket.marketIsEnabled && !takerMarket.offerConfig.isEnabled && takerMarket.eligibleInvoiceCount === 0) {
    return {
      bgColor: 'bg-deepBlue-50',
      bgNotificationColor: 'bg-deepBlue-500/20',
      heading: t('offerDetails.marketHasNoEligibleInvoices', { marketName: getTakerMarketName(takerMarket) }),
      subHeading: t('offerDetails.marketFutureOfferSubHeading'),
    };
  }

  // An express-accept offer can have all invoices removed from clear, making isManuallyAwarded false even though the offer is still active
  if (
    (takerMarket.offerConfig.isManuallyAwarded || takerMarket.offerConfig.isActiveExpressAccept) &&
    takerMarket.offerConfig.isEnabled
  ) {
    return {
      bgColor: 'bg-deepBlue-50',
      bgNotificationColor: 'bg-deepBlue-500/20',
      heading: t('offerDetails.marketEarlyProcessing', { marketName: getTakerMarketName(takerMarket) }),
      subHeading: t('offerDetails.marketEarlyProcessingSubHeading'),
    };
  }

  // market is enabled, participation is on, no eligible invoices
  if (takerMarket.marketIsEnabled && takerMarket.offerConfig.isEnabled && takerMarket.eligibleInvoiceCount === 0) {
    return {
      bgColor: 'bg-deepBlue-50',
      bgNotificationColor: 'bg-deepBlue-500/20',
      heading: t('offerDetails.marketHasNoEligibleInvoices', { marketName: getTakerMarketName(takerMarket) }),
      subHeading: t('offerDetails.marketHasNoEligibleInvoicesFutureOffer'),
    };
  }

  // participation is off
  if (!takerMarket.offerConfig.isEnabled) {
    const hasOfferSchedule = !!takerMarket.offerSchedule && takerMarket.offerSchedule.type !== 'renewal';

    return {
      bgColor: 'bg-deepGray-100',
      bgNotificationColor: 'bg-deepGray-500/20',
      heading: hasOfferSchedule ? t('offerDetails.scheduledOfferNotActive') : t('offerDetails.turnOnParticipation'),
      subHeading: hasOfferSchedule
        ? t('offerDetails.scheduledOfferNotActiveInfo')
        : t('offerDetails.percentInvoicesCurrentlyAccepted', { formattedPercent: asPercent({ value: 0 }) }),
    };
  }

  if (percentClearing <= 1 && percentClearing >= 0.9) {
    return {
      bgColor: 'bg-canvas-green',
      bgNotificationColor: 'bg-primary-500/20',
      heading: t('offerDetails.highLikelihoodOfAcceptance'),
      subHeading: t('offerDetails.percentInvoicesCurrentlyAccepted', {
        formattedPercent: asPercent({ value: percentClearing }),
      }),
    };
  }

  if (percentClearing < 0.9 && percentClearing >= 0.5) {
    return {
      bgColor: 'bg-yellow-100',
      bgNotificationColor: 'bg-yellow-500/20',
      heading: t('offerDetails.partialAcceptance'),
      subHeading: t('offerDetails.percentInvoicesCurrentlyAccepted', {
        formattedPercent: asPercent({ value: percentClearing }),
      }),
    };
  }

  if (percentClearing < 0.5) {
    return {
      bgColor: 'bg-red-100',
      bgNotificationColor: 'bg-red-500/20',
      heading: t('offerDetails.unlikelyAcceptance'),
      subHeading: t('offerDetails.percentInvoicesCurrentlyAccepted', {
        formattedPercent: asPercent({ value: percentClearing }),
      }),
    };
  }

  return {
    bgColor: 'bg-deepGray-100',
    bgNotificationColor: 'bg-deepGray-500/20',
    heading: t('offerDetails.turnOnParticipation'),
    subHeading: t('offerDetails.percentInvoicesCurrentlyAccepted', { formattedPercent: asPercent({ value: 0 }) }),
  };
};

const OfferDetailsBannerNotification = ({
  actionSlot,
  bgColor,
  heading,
  subHeading,
}: {
  actionSlot?: React.ReactNode;
  bgColor: string;
  heading: string;
  subHeading: string;
}) => {
  return (
    <div className={cn('flex flex-col justify-between gap-2 rounded-md p-4 lg:flex-row lg:items-center', bgColor)}>
      <div className="flex shrink-0 items-center gap-2">
        <CalendarLinesIcon className="h-5 w-5 fill-text-primary" />
        <span className="font-medium">{heading}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span>{subHeading}</span>
        {actionSlot}
      </div>
    </div>
  );
};

const OfferDetailsBannerComponent = ({ takerMarket }: { takerMarket: TakerMarket }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const enablePricing = useEnablePricing();
  const { openModal, openAutoRenewalModal } = useOfferModalContext();
  const { openModal: openEditRateModal } = useEditRateModalContext();
  const { openModal: openEditScheduleModal } = useEditScheduleModalContext();
  const enableUnification = useEnableUnification();
  const enableAutomations = useEnableAutomations();
  const navigationState = getOfferBuilderNavigationState([takerMarket], { expandSelected: true });
  const {
    bgColor: bgColorContent,
    bgNotificationColor: bgNotificationColorContent,
    heading,
    subHeading,
  } = useOfferDetailsContent(takerMarket);
  const { listenToMarketStats } = useMarketStatsStatus();
  const listeningForMarketStat = listenToMarketStats([
    {
      marketUuid: takerMarket.marketUuid,
      takerId: takerMarket.takerDivisionId,
      takerDivisionUuid: takerMarket.takerDivisionUuid,
      makerOrganizationUuid: takerMarket.makerOrganizationUuid,
    },
  ]);
  const bgColor = listeningForMarketStat ? 'bg-deepGray-100' : bgColorContent;
  const bgNotificationColor = listeningForMarketStat ? 'bg-deepGray-500/20' : bgNotificationColorContent;
  const hasOfferSchedule = !!takerMarket.offerSchedule && takerMarket.offerSchedule.type !== 'renewal';
  const hasPreferredOffer = takerMarket.offerConfig.offerType === 'PREFERRED_TERM';
  const { renewalIntervalDays } = getRenewalSchedule(takerMarket);

  const handleModifyOffer = () => {
    if (enableUnification && enablePricing) {
      // if active offer or has offer schedule, open edit rate modal
      // if no active offer, navigate to build offer
      if (takerMarket.offerConfig.isEnabled || hasOfferSchedule) {
        openEditRateModal(takerMarket);
      } else {
        navigate(asEnterpriseLink('build-offer'), { state: navigationState });
      }
    } else {
      openModal({ takerMarket, initialAccordionValue: 'Rate' });
    }
  };

  const handleModifySchedule = () => {
    if (enableUnification) {
      openEditScheduleModal(takerMarket);
    } else {
      openModal({ takerMarket, initialAccordionValue: 'Frequency' });
    }
  };

  return (
    <>
      <div className={cn('rounded-2xl bg-deepGray-100 p-6', bgColor)}>
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
          <div className="max-w-md space-y-2">
            <div className="text-lg font-medium">
              {listeningForMarketStat ? t('taker.marketFeedback.calculatingYourMarket') : heading}
            </div>
            {subHeading && (
              <div className={cn('text-sm font-medium text-text-secondary', { 'blur-sm': listeningForMarketStat })}>
                {subHeading}
              </div>
            )}
          </div>
          <div>
            <OfferContent
              hasOfferSchedule={hasOfferSchedule}
              openOfferModal={handleModifyOffer}
              takerMarket={takerMarket}
            />
          </div>
        </div>
        {(hasPreferredOffer || hasOfferSchedule) && (
          <div className="mt-6 space-y-2">
            {hasPreferredOffer ? (
              <OfferDetailsBannerNotification
                actionSlot={
                  <IconButton
                    className="hover:bg-black/10"
                    icon={PenIcon}
                    name={t('offer.autoRenew.modify')}
                    onClick={() => openAutoRenewalModal(takerMarket)}
                    size="sm"
                    variant="ancillary"
                  />
                }
                bgColor={bgNotificationColor}
                heading={t('offer.autoRenew')}
                subHeading={
                  renewalIntervalDays
                    ? `${t('core.enabled')} - ${t('offer.autoRenew.termDays', { renewalIntervalDays })}`
                    : t('core.disabled')
                }
              />
            ) : null}
            {hasOfferSchedule ? (
              <IntercomDataTarget target="divisionDetailsOfferSchedule">
                <OfferDetailsBannerNotification
                  bgColor={bgNotificationColor}
                  heading={t('scheduling.schedule')}
                  subHeading={buildCustomScheduleSentenceFromExistingOfferSchedule({
                    offerSchedule: takerMarket.offerSchedule!,
                    t,
                  })}
                  {...(enableAutomations
                    ? {
                        actionSlot: (
                          <IconButton
                            className="hover:bg-black/10"
                            icon={PenIcon}
                            name={t('scheduling.modifySchedule')}
                            onClick={handleModifySchedule}
                            size="sm"
                            variant="ancillary"
                          />
                        ),
                      }
                    : {})}
                />
              </IntercomDataTarget>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
};

const OfferDetailsBanner = (props: { takerMarket: TakerMarket }) => {
  return (
    <OfferModalWithContext>
      <EditRateModalWithContext>
        <EditScheduleModalWithContext>
          <QueryBoundaries LoadingComponent={() => <Skeleton className="h-40" />}>
            <OfferDetailsBannerComponent {...props} />
          </QueryBoundaries>
        </EditScheduleModalWithContext>
      </EditRateModalWithContext>
    </OfferModalWithContext>
  );
};

export default OfferDetailsBanner;
